import { initPhoneInput, getSelectedCountryCode, getE164, getInstanceForInput } from './utils/intl-tel.js';
import { getCookie, setCookie } from './utils/cookies.js';
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import { sha256 } from './utils/hash-sha256';

class ApplicationForm extends HTMLElement {
    constructor() {
        super();

        const defaultOptions = {
            translations: {
                isFieldRequired: 'This field is required.',
                emailInvalid: 'Please enter a valid email address.',
                emailTypo: 'A typo has been found in the email address. Please check for any spelling mistakes and try again. Perhaps you meant',
                emailUndeliverable: 'The entered email address is not recognized. Perhaps you made a typo?',
                emailNetworkProblem: 'There was a network problem while validating your email. Please try again later.',
            },
        };
        const options = this.dataset.options ? JSON.parse(this.dataset.options) : {};
        this.options = {
            ...defaultOptions,
            ...options,
            translations: {
                ...defaultOptions.translations,
                ...options?.translations,
            },
        };

        this.selectors = {
            form: '[data-form]',
            field: '[data-form-field]',
            fieldWithName: (name) => `[data-form-field=${name}]`,
            fieldLabel: '[data-form-field-label]',
            fieldInput: '[data-form-field-input]',
            fieldError: '[data-form-field-error]',
            required: '[required]',
            submit: '[data-form-submit]',
            motivationCharacterInput: '[data-motivation-character-input]',
            motivationCharacterCount: '[data-motivation-character-count]',
        };

        // All JS-driven state lives here as data attributes instead of classes.
        // CSS hooks into these directly, e.g. `[data-invalid="true"] { ... }`
        this.attrs = {
            invalid: 'data-invalid',   // on a field input, "true" while invalid
            loading: 'data-loading',   // on the submit button, "true" while submitting
            visible: 'data-visible',   // on a field container / error element, "true" | "false"
        };

        // Cache SparkPost results per email value, so the same value isn't
        // re-validated remotely twice (e.g. once on blur, once on submit).
        this.emailValidationCache = new Map();
    }

    connectedCallback() {
        this.submitBtnElem = this.querySelector(this.selectors.submit);

        // Disable submit button during initialization
        this.toggleSubmitButton(false);

        // Initialize form
        this.formElem = this.querySelector(this.selectors.form);

        if (!this.formElem) {
            console.error('No form element found');
            return;
        }

        // Disable native validation
        this.formElem.setAttribute('novalidate', '');

        // Add validation listeners to all inputs
        this.addFieldValidationListeners();

        // Add motivation character counter
        this.addMotivationCharacterCountListener();

        // Add submit handler
        this.formElem.addEventListener('submit', this.handleSubmit.bind(this));

        // Add logic for inputs of type file
        this.querySelectorAll('input[type="file"]').forEach((fileInput) => fileInput.addEventListener('change', this.handleFileInputChange.bind(this)));

        // Reset submit button
        this.submitting = false;
        this.submitBtnElem?.removeAttribute(this.attrs.loading);
        this.toggleSubmitButton(true);

        initPhoneInput(this.selectors.fieldWithName('phone'), this.options?.phoneValidation);

        // initialise intl-tel-input on any tel fields
        this.formElem
            .querySelectorAll('[data-form-field] input[type="tel"]')
            .forEach((telInput) => {
                const fieldName = telInput.closest(this.selectors.field)?.getAttribute('data-form-field');
                if (fieldName && fieldName !== 'phone') {
                    initPhoneInput(this.selectors.fieldWithName(fieldName), this.options?.phoneValidation);
                }
            });
    }

    toggleSubmitButton(enable) {
        if (this.submitBtnElem) {
            this.submitBtnElem.disabled = !enable;
        }
    }

    async handleSubmit(event) {
        event.preventDefault();

        // Validate all fields
        const isValid = await this.validateForm();

        if (isValid) {
            try {
                await this.submitForm();
            } catch (error) {
                console.error('Form submission error:', error);
                // Show error state if needed
            }
        } else {
            // Focus on first invalid field
            const firstInvalid = this.formElem.querySelector(`[${this.attrs.invalid}="true"]`);
            if (firstInvalid) firstInvalid.focus();
        }
    }

    addFieldValidationListeners() {
        const inputs = this.formElem.querySelectorAll(this.selectors.fieldInput);

        inputs.forEach(input => {
            // Validate on blur (when leaving field)
            input.addEventListener('blur', () => this.validateFieldWithRemoteChecks(input));

            // Clear error on input (while typing)
            input.addEventListener('input', () => {
                if (input.hasAttribute(this.attrs.invalid)) {
                    this.clearError(input);
                }

                // Ignore empty input events triggered during initialization
                if (!input.value.trim()) {
                    return true;
                }
            });
        });
    }

    addMotivationCharacterCountListener() {
        const input = this.querySelector(this.selectors.motivationCharacterInput);
        const count = this.querySelector(this.selectors.motivationCharacterCount);

        if (!input || !count) {
            return;
        }

        const updateCount = () => {
            count.textContent = input.value.length;
        };

        input.addEventListener('input', updateCount);

        updateCount();
    }

    validateField(input) {
        const inputValue = typeof input.value === 'string' ? input.value.trim() : input.value;

        // Skip validation if field isn't required and empty
        if (!input.hasAttribute('required') && !inputValue) {
            return true;
        }

        let isValid = true;
        let errorMessage = '';

        // Check required fields
        if (input.hasAttribute('required') && input.type !== 'checkbox' && input.type !== 'radio' && !inputValue) {
            isValid = false;
            errorMessage = this.options.translations.isFieldRequired;
        }

        if (input.type === 'checkbox' && input.hasAttribute('required') && !input.checked) {
            isValid = false;
            errorMessage = this.options.translations.isFieldRequired;
        }

        if (input.type === 'radio' && input.hasAttribute('required')) {
            const radioInputs = input.closest(this.selectors.field)?.querySelectorAll('input[type="radio"]');
            const hasChecked = Array.from(radioInputs).filter(({ checked }) => checked).length > 0;

            if (!hasChecked) {
                isValid = false;
                errorMessage = this.options.translations.isFieldRequired;
            }
        }

        else if (input.type === 'email' && !this.validateEmail(inputValue)) {
            isValid = false;
            errorMessage = this.options.translations.emailInvalid;
        }

        else if (input.type === 'tel' && !this.validatePhone(inputValue, input)) {
            isValid = false;
            errorMessage = this.options.translations.phoneInvalid;
        }

        else if (input.name === 'zipcode' && !this.validateZipcode(inputValue)) {
            isValid = false;
            errorMessage = this.options.translations.zipCodeInvalid;
        }

        if (!isValid) {
            this.showError(input, errorMessage);
        } else {
            this.clearError(input);
        }

        return isValid;
    }

    /**
     * Runs the synchronous validateField() checks and, for `type="email"`
     * fields, also the async SparkPost validation. Used for both blur
     * (real-time feedback) and form submission, so any email field is
     * validated the same way everywhere.
     *
     * @param {HTMLInputElement} input
     * @returns {Promise<boolean>}
     */
    async validateFieldWithRemoteChecks(input) {
        const isValid = this.validateField(input);

        if (!isValid || input.type !== 'email') {
            return isValid;
        }

        return this.validateEmailWithSparkPost(input);
    }

    async validateForm() {
        let isValid = true;
        const inputs = this.formElem.querySelectorAll(this.selectors.fieldInput);
        const emailInputs = Array.from(inputs).filter(({ type }) => type === 'email');

        for (const input of inputs) {
            const fieldIsValid = this.validateField(input);
            if (!fieldIsValid) {
                isValid = false;
            }
        }

        // Exit early if invalid up to now, so we don't even ping the SparkPost validation
        if (!isValid) {
            return false;
        }

        // SparkPost validation for every email field in the form
        for (const emailInput of emailInputs) {
            const isSparkPostValid = await this.validateEmailWithSparkPost(emailInput);
            if (!isSparkPostValid) {
                isValid = false;
            }
        }

        return isValid;
    }

    validateEmail(email) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    }

    async validateEmailWithSparkPost(input) {
        const email = input.value.trim();

        // Reuse a previous result for this exact value instead of re-hitting the API.
        if (this.emailValidationCache.has(email)) {
            const { isValid, errorMessage } = this.emailValidationCache.get(email);
            if (isValid) {
                this.clearError(input);
            } else {
                this.showError(input, errorMessage);
            }
            return isValid;
        }

        let isValid = true;
        let errorMessage = '';

        try {
            const response = await fetch('https://proindustry-recipient-validation.support-51c.workers.dev/', {
                method: 'POST',
                body: JSON.stringify({ email: email }),
            });

            const rawText = await response.text();

            if (!response.ok) {
                throw new Error(`Worker responded with HTTP ${response.status}`);
            }

            const responseDataResults = JSON.parse(rawText);
            if (responseDataResults?.errors) {
                console.warn('[SparkPost] Worker returned errors:', responseDataResults.errors);
                errorMessage = this.options.translations.emailInvalid;
                isValid = false;
            } else {
                const result = responseDataResults?.results?.result;
                switch (result) {
                    case 'typo':
                        errorMessage = `${this.options.translations.emailTypo} '${responseDataResults?.results?.did_you_mean || ''}'`;
                        isValid = false;
                        break;
                    case 'undeliverable':
                        errorMessage = this.options.translations.emailUndeliverable;
                        isValid = false;
                        break;
                    default:
                        isValid = true;
                }
            }
        } catch (error) {
            console.error('[SparkPost] Validation failed:', error);
            errorMessage = this.options.translations.emailNetworkProblem;
            isValid = false;
        }

        if (!isValid) {
            this.showError(input, errorMessage);
        } else {
            this.clearError(input);
        }

        // Don't cache network failures — allow a retry on the next check.
        if (errorMessage !== this.options.translations.emailNetworkProblem) {
            this.emailValidationCache.set(email, { isValid, errorMessage });
        }

        return isValid;
    }

    validatePhone(phone, input) {
        // Resolve the intl-tel-input instance for this specific tel field
        const telInput = input ?? this.formElem.querySelector(`${this.selectors.fieldWithName('phone')} input`);
        const iti = telInput && getInstanceForInput(telInput);
        if (iti) {
            return iti.isValidNumber();
        }
        // Fallback basic regex
        const re = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,3}[-\s.]?[0-9]{2,4}[-\s.]?[0-9]{2,4}$/;
        return re.test(String(phone).toLowerCase());
    }

    validateZipcode(zipcode) {
        const value = String(zipcode).trim();
        const countryCode = getSelectedCountryCode();

        if (!postcodeValidatorExistsForCountry(countryCode)) {
            return true;
        }

        return postcodeValidator(value, countryCode);
    }

    showError(input, message) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute(this.attrs.invalid, 'true');

        const errorElement = this.querySelector(this.selectors.fieldWithName(input.name))?.querySelector(this.selectors.fieldError);
        if (!errorElement) return;
        errorElement.textContent = message;
        errorElement.setAttribute(this.attrs.visible, 'true');
    }

    clearError(input) {
        input.removeAttribute('aria-invalid');
        input.removeAttribute(this.attrs.invalid);

        if (input.type === 'radio') {
            input.closest(this.selectors.field)?.querySelectorAll('input[type="radio"]').forEach((el) => el.removeAttribute(this.attrs.invalid));
        }

        const errorElement = this.querySelector(this.selectors.fieldWithName(input.name))?.querySelector(this.selectors.fieldError);
        if (!errorElement) return;
        errorElement.innerHTML = '';
        errorElement.setAttribute(this.attrs.visible, 'false');
    }

    clearAllErrors() {
        this.formElem.querySelectorAll(this.selectors.fieldInput).forEach(input => this.clearError(input));
    }

    handleFileInputChange(event) {
        const files = event.target.files;
        const fieldContainer = event.target.closest(this.selectors.field);
        const displayFileNameInputElem = fieldContainer?.querySelector('input[type="hidden"]');
        const cvFilenameElem = fieldContainer?.querySelector('[data-cv-filename]');

        if (!displayFileNameInputElem) {
            return;
        }

        if (files.length > 0) {
            const fileNames = Array.from(files).map(file => file.name).join(', ');
            displayFileNameInputElem.value = fileNames;
            if (cvFilenameElem) cvFilenameElem.textContent = fileNames;
        } else {
            displayFileNameInputElem.value = '';
            if (cvFilenameElem) cvFilenameElem.textContent = '';
        }

        const file = event.target.files[0];
        if (!file) return;

        // Check file size directly using native file size property
        const maxSize = 8 * 1024 * 1024; // 8 MB

        if (file.size > maxSize) {
            // File exceeds limit
            displayFileNameInputElem.value = 'File too big';
            if (cvFilenameElem) cvFilenameElem.textContent = '';

            // Clear the file input
            event.target.value = '';

            // Show error to user
            this.showError(event.target, this.options.translations.fileSizeTooBig);
        }
    }
    getGAClientID() {
        const cookie = {};
        document.cookie.split(';').forEach(function(el) {
            const splitCookie = el.split('=');
            const key = splitCookie[0].trim();
            const value = splitCookie[1];
            cookie[key] = value;
        });
        return cookie["_ga"]?.substring(6) ?? '';
    }
    getLocalISOString(date = new Date()) {
        const pad = (n) => String(n).padStart(2, '0');

        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());

        const offsetMinutes = -date.getTimezoneOffset(); // e.g. +120 for UTC+2
        const sign = offsetMinutes >= 0 ? '+' : '-';
        const offsetHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
        const offsetMins = pad(Math.abs(offsetMinutes) % 60);

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
    }
    pushDataLayerSubmitSuccess() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'submit_application',
            form_name: 'job_apply_form',
            form_action: 'submit_success',
            job_id: String(this.options.jobId ?? ''),
        });
    }

    async submitForm() {
        if (this.submitting) return;
        this.submitting = true;
        this.submitBtnElem?.setAttribute(this.attrs.loading, 'true');
        this.toggleSubmitButton(false);

        const formData = new FormData(this.formElem);

        // Set every tel field (e.g. "phone", "referrerPhone") to full E.164
        // format (e.g. +31685409115) from its own intl-tel-input instance.
        const telInputs = this.formElem.querySelectorAll('[data-form-field] input[type="tel"]');

        for (const telInput of telInputs) {
            const iti = getInstanceForInput(telInput);
            const e164 = iti && await getE164(iti);
            if (e164) {
                formData.set(telInput.name, e164);
            }
        }

        // Add session data
        const rawSession = getCookie('proindustry-session');
        this.sessionCookie = rawSession ? JSON.parse(rawSession) : {};

        // add extra data
        formData.set('atsId', this.options.atsId ?? '');
        formData.set('jobId', this.options.jobId ?? '');
        formData.set('formType', this.options.formType);
        formData.set('officeId', formData.get('officeId') || this.options.officeId || '');
        formData.set('timestamp', this.getLocalISOString());
        formData.append('clientId', this.getGAClientID());

        formData.append('referer', this.sessionCookie?.source ?? 'Direct');
        formData.append('utm_campaign', this.sessionCookie?.UTM_Campaign ?? '');
        formData.append('utm_content', this.sessionCookie?.UTM_Content ?? '');
        formData.append('utm_medium', this.sessionCookie?.UTM_Medium ?? '');
        formData.append('utm_source', this.sessionCookie?.UTM_Source ?? '');
        formData.append('utm_term', this.sessionCookie?.UTM_Term ?? '');
        const restNonce = this.options?.restNonce
            ?? ((window.wpApiSettings && window.wpApiSettings.nonce) ? window.wpApiSettings.nonce : '');
        if (restNonce) {
            formData.append('nonce', restNonce);
        }
        // console.log(
        //     'Form payload test me now:\n' +
        //     JSON.stringify(
        //         Object.fromEntries(
        //             [...formData.entries()].map(([key, value]) => [
        //                 key,
        //                 value instanceof File
        //                     ? { name: value.name, size: value.size, type: value.type }
        //                     : value,
        //             ])
        //         ),
        //         null,
        //         2
        //     )
        // );

        try {
            const response = await fetch('/wp-json/api/v1/jobApplication', {
                method: 'POST',
                headers: restNonce ? {'X-WP-Nonce': restNonce} : {},
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Application request failed: ${response.status}`);
            }

            this.pushDataLayerSubmitSuccess();

            window.location.href = this.options.successUrl; // redirect to thank you page
        } catch (error) {
            console.error('Form submission error:', error);
            this.submitting = false;
            this.submitBtnElem?.removeAttribute(this.attrs.loading);
            this.toggleSubmitButton(true);
        } finally {
            this.clearAllErrors();
        }
    }


    disconnectedCallback() {
        // Cleanup form submit listener
        this.formElem?.removeEventListener('submit', this.handleSubmit);

        // Cleanup file input listeners
        this.querySelectorAll('input[type="file"]').forEach((fileInput) => {
            fileInput.removeEventListener('change', this.handleFileInputChange);
        });

        // Cleanup field validation listeners (blur/input)
        if (this.formElem) {
            const inputs = this.formElem.querySelectorAll(this.selectors.fieldInput);
            inputs.forEach(input => {
                input.removeEventListener('blur', this.validateField);
                input.removeEventListener('input', this.clearError);
            });
        }
    }
}
customElements.define('application-form', ApplicationForm);